import { useEffect, useState } from 'react'
import { Button, Modal, Spinner } from 'react-bootstrap'
import mammoth from 'mammoth'
import { XlsxViewerPreview } from './extend/xlsx-viewer'
import { showKAlert } from '../helpers'
import { viewKycFile } from '../helpers/viewKycFile'
import { t } from '../locale'

export interface PreviewTarget {
  clientId: string
  firmename: string
  type: string
  filename: string
}

type PreviewKind = 'pdf' | 'image' | 'txt' | 'excel' | 'word' | null

interface KFilePreviewProps {
  // Set by the parent when a preview button is clicked; back to null on close.
  target: PreviewTarget | null
  onClose: () => void
}

// Authenticated KYC file preview, mirroring DocumentsForm: PDFs and images render
// inline, .txt/.csv as text, .xlsx/.xls through the sheet viewer, .docx via mammoth,
// anything else opens as a raw download in a new tab.
export default function KFilePreview({ target, onClose }: KFilePreviewProps) {
  const [kind, setKind] = useState<PreviewKind>(null)
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [text, setText] = useState<string | null>(null)
  const [html, setHtml] = useState<string | null>(null)
  const [buffer, setBuffer] = useState<ArrayBuffer | null>(null)
  const [loading, setLoading] = useState(false)
  const [isDark, setIsDark] = useState(false)

  const reset = () => {
    setObjectUrl(prev => {
      if (prev) window.URL.revokeObjectURL(prev)
      return null
    })
    setKind(null)
    setText(null)
    setHtml(null)
    setBuffer(null)
    setLoading(false)
  }

  const close = () => {
    reset()
    onClose()
  }

  useEffect(() => {
    if (!target) {
      reset()
      return
    }

    let cancelled = false

    const openInNewTab = (blob: Blob, contentType: string) => {
      const url = window.URL.createObjectURL(new Blob([blob], { type: contentType }))
      window.open(url, '_blank')
      setTimeout(() => window.URL.revokeObjectURL(url), 10000)
      onClose()
    }

    const run = async () => {
      const result = await viewKycFile(
        target.clientId,
        target.firmename,
        target.type,
        target.filename
      )
      if (cancelled) return
      if (!result) {
        showKAlert('danger', 'previewError')
        onClose()
        return
      }

      const { blob, contentType } = result
      const extension = target.filename.split('.').pop()?.toLowerCase() ?? ''

      if (extension === 'pdf') {
        setObjectUrl(
          window.URL.createObjectURL(new Blob([blob], { type: contentType || 'application/pdf' }))
        )
        setKind('pdf')
        return
      }

      if (['png', 'jpg', 'jpeg', 'tiff', 'tif'].includes(extension)) {
        setObjectUrl(window.URL.createObjectURL(new Blob([blob], { type: contentType })))
        setKind('image')
        return
      }

      if (extension === 'txt' || extension === 'csv') {
        setText(await blob.text())
        setKind('txt')
        return
      }

      if (extension === 'xlsx' || extension === 'xls') {
        setBuffer(await blob.arrayBuffer())
        setKind('excel')
        return
      }

      if (extension === 'docx') {
        setKind('word')
        setLoading(true)
        const converted = await mammoth.convertToHtml({ arrayBuffer: await blob.arrayBuffer() })
        if (cancelled) return
        setHtml(converted.value)
        setLoading(false)
        return
      }

      // legacy .doc and anything without an in-app renderer
      openInNewTab(blob, contentType)
    }

    run().catch(() => {
      if (cancelled) return
      showKAlert('danger', 'previewError')
      onClose()
    })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target])

  return (
    <Modal show={!!kind} onHide={close} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>{target?.filename ?? t('preview')}</Modal.Title>
        {kind === 'excel' && (
          <Button
            variant='outline-secondary'
            size='sm'
            className='ms-auto me-2'
            onClick={() => setIsDark(prev => !prev)}
          >
            <i className={`bi bi-${isDark ? 'sun' : 'moon'}`} />
          </Button>
        )}
      </Modal.Header>
      <Modal.Body style={{ height: '80vh', overflow: 'auto' }}>
        {loading && (
          <div className='d-flex justify-content-center align-items-center h-100'>
            <Spinner animation='border' />
          </div>
        )}

        {kind === 'pdf' && objectUrl && (
          <iframe
            src={objectUrl}
            title={t('preview')}
            width='100%'
            height='100%'
            style={{ border: 'none' }}
          />
        )}

        {kind === 'image' && objectUrl && (
          <div className='d-flex justify-content-center'>
            <img src={objectUrl} alt={target?.filename ?? ''} style={{ maxWidth: '100%' }} />
          </div>
        )}

        {!loading && kind === 'txt' && (
          <pre className='p-3 mb-0' style={{ whiteSpace: 'pre-wrap' }}>
            {text}
          </pre>
        )}

        {kind === 'excel' && buffer && (
          <XlsxViewerPreview
            file={buffer}
            fileName={target?.filename ?? undefined}
            height='100%'
            isDark={isDark}
          />
        )}

        {!loading && kind === 'word' && html && (
          <div className='p-2' dangerouslySetInnerHTML={{ __html: html }} />
        )}
      </Modal.Body>
    </Modal>
  )
}
